import { Component, OnInit, ViewChild, ChangeDetectionStrategy, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationCancel, NavigationEnd, NavigationError, PRIMARY_OUTLET, ParamMap, Router, RouterLink, UrlTree } from '@angular/router';
import dayjs from 'dayjs';
import { Observable, Subject, of, fromEvent, concat, forkJoin, BehaviorSubject, combineLatest, Subscription } from "rxjs";
import { debounceTime, map, distinctUntilChanged, filter, take, takeUntil } from "rxjs/operators";
import { FormArray, FormGroup, FormControl, FormBuilder, FormsModule } from '@angular/forms';

import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';


//import * as constants from "@app/shared/constants";
import { SalesOrderState, SALES_DEFAULT_CUSTOMER_ORDERS_YEARS } from '@app/shared/constants-server';
import { salesOrderLabels, salesOrderColors, getOrderLabel, getDocTypeLabel, DocType, getStateKey, EVENTTYP_INSERT, EVENTTYP_UPDATE, ObjectType } from "@app/shared/constants";
import { ProductsService } from '@app/sales/products/products.service';
import { QuotationsService } from '@app/sales/quotations/quotations.service';
import { SpinnerService } from '@app/shared/spinner.service';
import { AlertService } from '@app/shared/alert.service';
import { DatePipe, KeyValuePipe, NgClass, DecimalPipe } from '@angular/common';

import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonService } from '@app/common/common.service';
import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { QuotationdetailsComponent, HoveredOrder, parentOrderLabel } from '@app/sales/quotations/quotationdetails/quotationdetails.component';
import { SearchPartnerComponent } from '@app/shared/search-partner/search-partner.component';
import { MatMenuModule } from '@angular/material/menu';
import { Store, User, Station, Partner } from '@app/shared/_models/common';

import { SelectDateComponent } from '@app/shared/select-date/select-date.component';
import { SmartInputComponent } from '@app/shared/smart-input/smart-input.component';
import { PartnerService } from '@app/common/partner/partner.service';
import { pickedDateStr, yearsBefore } from '@app/shared/time';

import { QuotationsFilter } from './quotations.service'

// Paramètres d'URL illisibles : ignorés un par un, jamais d'erreur (jour réel AAAA-MM-JJ, id entier positif sur 32 bits).
function urlDay(v: string | null): Date | null {
  return v && /^\d{4}-\d{2}-\d{2}$/.test(v) && dayjs(v).isValid() && dayjs(v).format('YYYY-MM-DD') === v ? dayjs(v).toDate() : null;
}

function urlId(v: string | null): string | null {
  return v !== null && /^\d{1,10}$/.test(v) && +v > 0 && +v <= 2147483647 ? String(+v) : null;
}

type Period = 'day' | 'month' | 'days30' | 'months12';

// Date neuve : les Date du filtre sont partagées par référence (Originalfilter), jamais réutilisées telles quelles.
function copyDay(d?: Date): Date | undefined {
  return d ? new Date(d) : undefined;
}

function pathOf(t: UrlTree): string {
  return t.root.children[PRIMARY_OUTLET]?.segments.map((s) => s.path).join('/') ?? '';
}


@Component({
  selector: 'app-quotations',
  standalone: true,
  templateUrl: './quotations.component.html',
  styleUrls: ['./quotations.component.scss', /* '../../styles-custom.scss'*/],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SelectDateComponent, SmartInputComponent, MatDatepickerModule, MatMenuModule, NgClass, SearchPartnerComponent, QuotationdetailsComponent, MenuCommonComponent, MatButtonModule, MatTooltipModule, RouterLink, MatFormFieldModule, MatInputModule, FormsModule, MatIconModule, DatePipe, KeyValuePipe, DecimalPipe, MatProgressSpinnerModule]
})
export class QuotationsComponent {
  public orders: any[] = [];
  public displayedColumnsInfo: [string, string, string, boolean?][] = [
    ['id', 'Id', 'size1'],
    ['datecre', 'Dat. Cré', 'size2'],
    ['dateval', 'Dat. M.A.J', 'size2'],
    ['type', 'Type', 'size0'],
    ['store', 'E', 'size1'],
    ['station', 'P', 'size1'],
    ['user', 'Emp.', 'size1'],
    ['status', 'Statut', 'size1'],
    ['customer', 'Client', 'size2'],
    ['amount_taxi', 'Tot.', 'size1'],
    ['amount_remainer', 'Dû', 'size1'],
  ];
  public SalesOrderState = SalesOrderState;
  public salesOrderColors = salesOrderColors;
  public salesOrderLabels = salesOrderLabels;
  public getLabel = getOrderLabel;
  // public searchname: string = "";		// Name Search field // old cno
  // public searchstate: string = "-1";	// State Search field
  // public searchstate: number = 1;	// State Search field
  public selected_id: number | null = null;
  public mouseoverSubject: Subject<HoveredOrder> = new Subject();
  //public constants = constants;
  public mouseoutSubject: Subject<void> = new Subject();
  public stores: Store[] = [{ id: -1, name: 'Tous' }];
  /** Légende id → nom des établissements, affichée en infobulle sur l'entête « E ». */
  public storeLegend = '';
  public stations: Station[] = [{ id: -1, name: 'Tous' }];
  public users: User[] = [{ id: -1, store: -1, currentstore: 0, firstname: 'Tous' }];
  // public searchstore: number = 0;	// State Search field

  // « Illimité » DOIT être une borne explicite : quand la requête ne porte aucune
  // limite, le serveur en applique une de 100 (sales/src/order/model.rs, `if
  // limitclause.is_empty() { LIMIT 100 }`). Une valeur vide était donc silencieusement
  // tronquée à 100 commandes.
  public readonly UNLIMITED = '10000000';

  public limitsValue = ['50', '200', '1000', '5000', '10000', '50000', this.UNLIMITED];

  /** Libellé d'une limite : milliers séparés par une espace insécable. */
  public limitLabel(val: string): string {
    if (!val || val === this.UNLIMITED) return 'Illimité';
    return val.replace(/\B(?=(\d{3})+$)/g, '\u00A0');
  }

  // Arrivée sur l'écran : la journée en cours (Du = Au = aujourd'hui), 5000 pièces au plus.
  public Originalfilter: QuotationsFilter = { limit: '5000', dattyp: '-1', datstart: new Date(), datend: new Date(), state: '-1', store: '-1', user: '-1', doc_type: '-1' };
  public filter: QuotationsFilter = { ...this.Originalfilter };

  public customer: Partner | null = null;
  public is_loading = false;
  @ViewChild(QuotationdetailsComponent) quotationdetailsRef?: QuotationdetailsComponent;
  @ViewChild('orderIdNav') orderIdNavInput?: SmartInputComponent;
  public orderIdText = '';                    // texte du champ « Id commande »
  public orderIdHref: string | null = null;   // href de l'icône lien, recalculé à chaque frappe
  public orderId: string | null = null;       // id que la liste affiche seul (raccourci), null hors raccourci
  public searchFailed = false;                // dernière lecture en erreur : la liste vide ne prouve rien, Tab et Rechercher relancent

  private destroyRef = inject(DestroyRef);
  private partnerService = inject(PartnerService);
  private alive = true;
  private started = false;
  private ownWrites = 0;
  private syncPending = false;
  private storesLoaded = false;
  private usersLoaded = false;
  private searchSub?: Subscription;
  public listed = false;                      // la liste est le résultat d'une recherche sur la barre affichée ; faux dès qu'un filtre change
  private rangeBeforeCustomer: { datstart?: Date; datend?: Date } | null = null; // Du/Au d'avant le client, rendus quand on le retire
  private leaving = false;                    // Entrée dans « Id commande » ouvre la pièce : le blur qui suit ne cherche plus


  constructor(public commonService: CommonService, private quotationsService: QuotationsService, private router: Router, private route: ActivatedRoute, private fb: FormBuilder, private spinner: SpinnerService, private alertService: AlertService) { }

  ngOnInit(): void {
    this.alertService.clear();						// Why that? Because this page is the first page on "quotations" menu. So clear snackbar for nrt's 
    this.destroyRef.onDestroy(() => this.alive = false);
    // L'URL est la seule source des filtres : lue à l'ouverture, relue si elle change sous l'écran ouvert (lien du menu).
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((q) => this.applyUrl(q));

    this.commonService.getStores().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({ next: (stores: any) => { this.stores = [...this.stores, ...stores]; this.storeLegend = this.buildStoreLegend(); this.storesLoaded = true; this.reconcile(); } });     // destructuring because we want to keep the first element of the array that is "Tous" with id -1
    this.commonService.getStations().subscribe({ next: (stations: any) => { this.stations = [...this.stations, ...stations]; } });  // destructuring because we want to keep the first element of the array that is "Tous" with id -1
    this.commonService.getUsers().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({ next: (users: any) => { this.users = [...this.users, ...users]; this.usersLoaded = true; this.reconcile(); } });
  }

  ngAfterViewInit(): void {
    setTimeout(() => { if (!this.alive) return; this.started = true; this.onSearch(); });
    // Client porte tabindex=1 (search-partner) : l'Id prend le même rang, sinon Tab depuis lui sauterait Client.
    const idField = this.orderIdNavInput?.inputElement?.nativeElement;
    if (idField) idField.tabIndex = 1;
    requestAnimationFrame(() => this.orderIdNavInput?.focus());
  }

  private applyUrl(q: ParamMap): void {
    if (this.ownWrites) return;
    const before = this.filterKey();
    this.readUrl(q);
    this.normalize();
    if (!this.started) return;
    if (this.filterKey() !== before || !this.listed) this.onSearch(); else this.syncUrl(); // liste vidée par un filtre : l'URL relue la remplit
  }

  // Absent : le filtre d'arrivée (Originalfilter) ; vide (`from=`) : champ vide ; illisible ou inconnu : ignoré comme absent.
  private readUrl(q: ParamMap): void {
    const o = this.Originalfilter;
    // ?id=N est exclusif : la barre repart de l'état de Réinitialiser, les autres paramètres sont ignorés (et effacés par syncUrl).
    const id = urlId(q.get('id'));
    if (id) { this.enterOrderId(id); return; }
    const day = (k: string, dflt?: Date) => q.get(k) === '' ? undefined : urlDay(q.get(k)) ?? (dflt && new Date(dflt.getTime()));
    const pick = (k: string, allowed: string[], dflt?: string) => allowed.includes(q.get(k) ?? '') ? q.get(k)! : dflt;
    const customer = urlId(q.get('customer'));
    this.filter = {
      ...o,
      state: pick('state', this.STATE_RANK, o.state),
      doc_type: pick('type', this.docTypes, o.doc_type),
      store: urlId(q.get('store')) ?? o.store,
      user: urlId(q.get('user')) ?? o.user,
      datstart: day('from', o.datstart),
      datend: day('to', o.datend),
      limit: pick('limit', this.limitsValue, o.limit),
    };
    if (customer) this.filter.customer = customer;
    this.showCustomer(customer);
    this.rangeBeforeCustomer = null;
    this.orderId = null;
    this.showOrderId(null);
  }

  // L'URL ne porte que l'id du client : son nom est relu ; inconnu du serveur, le filtre tombe ; serveur muet, l'id reste affiché.
  private showCustomer(id: string | null): void {
    if (id === null) { this.customer = null; return; }
    if (this.customer && String(this.customer.id) === id) return;
    this.customer = null;
    this.partnerService.getPartnerByid(+id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (partners: Partner[]) => {
        if (this.filter.customer !== id) return;
        if (partners?.[0]?.id) { this.customer = partners[0]; return; }
        delete this.filter.customer;
        if (this.started) this.onSearch();
      },
      error: (e) => {
        console.error('[QuotationsComponent] client du filtre illisible', e);
        if (this.filter.customer === id) this.customer = { id: +id, name: 'Client n° ' + id } as Partner;
      },
    });
  }

  // Magasin ou employé absent du référentiel arrivé : « Tous », comme un paramètre illisible.
  private normalize(): void {
    const known = (list: { id?: number }[], v?: string) => !v || v === '-1' || list.some((x) => String(x.id) === v);
    if (this.storesLoaded && !known(this.stores, this.filter.store)) this.filter.store = '-1';
    if (this.usersLoaded && !known(this.users, this.filter.user)) this.filter.user = '-1';
  }

  private reconcile(): void {
    const before = this.filterKey();
    this.normalize();
    if (this.started && this.filterKey() !== before) this.onSearch();
  }

  // Seule forme écrite, donc celle d'un lien copié : défauts omis, sauf les jours (vides compris) qui datent le lien.
  private queryOf(): Record<string, string> {
    if (this.orderId) return { id: this.orderId }; // jamais « id= » vide : les URL attendues des NRT (ecransflow) n'en portent pas
    const f = this.filter, q: Record<string, string> = {};
    if (f.customer) q['customer'] = f.customer;
    if (f.state && f.state !== '-1') q['state'] = f.state;
    if (f.doc_type && f.doc_type !== '-1') q['type'] = f.doc_type;
    if (f.store && f.store !== '-1') q['store'] = f.store;
    if (f.user && f.user !== '-1') q['user'] = f.user;
    q['from'] = pickedDateStr(f.datstart) ?? '';
    q['to'] = pickedDateStr(f.datend) ?? '';
    if (f.limit !== this.Originalfilter.limit) q['limit'] = f.limit ?? '';
    return q;
  }

  private filterKey(): string {
    return JSON.stringify(this.queryOf());
  }

  // replaceUrl : aucun filtre n'ajoute d'entrée d'historique, Retour ramène à la page d'avant la liste ; jamais après destruction (l'URL serait celle de la page suivante).
  private syncUrl(): void {
    if (!this.alive || this.syncPending) return;
    // Navigation vers une autre page en cours (garde asynchrone, Retour) : naviguer l'annulerait ; on réécrit après sa fin si la liste est restée.
    // setTimeout : à l'événement de fin, currentNavigation() désigne encore la navigation qui s'achève.
    const nav = this.router.currentNavigation();
    if (nav && pathOf(nav.extractedUrl) !== pathOf(this.router.createUrlTree([]))) {
      this.syncPending = true;
      this.router.events.pipe(filter((e) => e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError), take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => setTimeout(() => { this.syncPending = false; this.syncUrl(); }));
      return;
    }
    const queryParams = this.queryOf();
    const cur = this.route.snapshot.queryParamMap;
    const keys = Object.keys(queryParams);
    if (cur.keys.length === keys.length && keys.every((k) => cur.get(k) === queryParams[k])) return;
    this.ownWrites++;
    this.router.navigate([], { queryParams, replaceUrl: true })
      .catch((e) => console.error('[QuotationsComponent] URL des filtres non écrite', e))
      .finally(() => this.ownWrites--);
  }

  onSearch(): void {
    this.listed = true;
    this.syncUrl();
    this.searchSub?.unsubscribe();
    this.is_loading = true;
    this.orders = [];
    this.searchFailed = false;
    this.quotationdetailsRef?.reset();
    // console.log('this.filter',this.filter);
    // Une réponse périmée (filtre changé entre-temps) n'écrase pas la liste : la requête précédente est abandonnée.
    // L'id part seul : côté serveur il écrase déjà tous les autres critères (sales/src/order/model.rs, bras "id").
    this.searchSub = this.quotationsService.getQuotationsByFilter(this.orderId ? { id: this.orderId } : this.filter).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (items: any) => {
          this.orders = items;
          this.is_loading = false;
          console.log('items', items);
        },
        error: (e) => {
          console.log("ERREUR Serveur : ", e.error);
          this.is_loading = false;
          this.searchFailed = true;
          this.alertService.error("ERREUR Serveur : " + e.error);
        }
      });
  }

  // Seul filtre qui relit à lui seul, et seulement au choix : le retrait ne lance rien (5 ans sans client = 5000 pièces pour rien).
  customerChange(customer: any): void {
    this.leaveOrderId();
    if (customer && customer.id && customer.name) {
      // Plage d'avant le premier client ; un client venu de l'URL sur sa plage automatique n'en a pas : celle d'arrivée.
      this.rangeBeforeCustomer ??= this.filter.customer && this.onCustomerRange() ? this.arrivalRange() : { datstart: copyDay(this.filter.datstart), datend: copyDay(this.filter.datend) };
      this.filter.customer = customer.id.toString();
      this.customer = customer;
      // Client choisi : ses N dernières années ; en Location « Du » reste vide comme dans onDocTypeChange (dossiers repris d'orthop dès 2015).
      this.filter.datstart = this.isRentalFilter ? undefined : yearsBefore(new Date(), SALES_DEFAULT_CUSTOMER_ORDERS_YEARS);
      this.filter.datend = new Date();
      this.onSearch();
      return;
    }
    // Retrait : Du/Au d'avant le client s'ils sont restés ceux posés au choix ; des jours retouchés depuis sont un choix, gardés.
    const before = this.rangeBeforeCustomer ?? this.arrivalRange();
    if (this.filter.customer && this.onCustomerRange()) {
      this.filter.datstart = this.isRentalFilter ? undefined : copyDay(before.datstart);
      this.filter.datend = copyDay(before.datend);
    }
    this.rangeBeforeCustomer = null;
    this.customer = null;
    delete this.filter.customer;
    this.clearList();
  }

  // Du/Au sont ceux qu'un choix de client pose : N ans (ou Du vide en Location) jusqu'à aujourd'hui.
  private onCustomerRange(): boolean {
    const today = new Date();
    const from = this.isRentalFilter ? null : pickedDateStr(yearsBefore(today, SALES_DEFAULT_CUSTOMER_ORDERS_YEARS));
    return pickedDateStr(this.filter.datstart) === from && pickedDateStr(this.filter.datend) === pickedDateStr(today);
  }

  private arrivalRange(): { datstart?: Date; datend?: Date } {
    return { datstart: new Date(), datend: new Date() }; // jours d'Originalfilter, en Date neuves
  }

  onMouseOver(row: any) {
    this.mouseoverSubject.next({
      id: row.id,
      doc_type: row.doc_type,
      parent_order_id: this.getParentOrderId(row),
      doc_ref: row.data?.doc_ref,
    });
  }

  onMouseOut() {
    this.mouseoutSubject.next();
  }

  onQuotationShown(id: number) {
    this.selected_id = id;
  }

  // La croix vide la barre et la liste, sans relancer : rien ne part avant « Rechercher ».
  onCancel(): void {
    this.resetBar();
    this.orderId = null;
    this.showOrderId(null);
    this.clearList();
  }

  // Un filtre a changé : la liste ne lui correspond plus, elle se vide (recherche en vol comprise) ; rien ne part avant « Rechercher ».
  private clearList(): void {
    this.searchSub?.unsubscribe();
    this.is_loading = false;
    this.listed = false;
    this.searchFailed = false;
    this.orders = [];
    this.quotationdetailsRef?.reset();
  }

  // État de Réinitialiser : « Du » vide, « Au » aujourd'hui (plage ouverte jusqu'à ce jour), sans client.
  // Dates neuves : celles d'Originalfilter sont partagées par référence avec le filtre courant.
  private resetBar(): void {
    this.filter = { ...this.Originalfilter, datstart: undefined, datend: new Date() };
    this.customer = null;
    this.rangeBeforeCustomer = null;
  }

  // --- Raccourci « Id commande » (calqué sur « Id produit » du sélecteur de produits) ----------------
  // Tab affiche la pièce seule dans la liste, Entrée ouvre sa fiche, l'icône l'ouvre dans un nouvel onglet.

  private fieldId(value: string): string | null {
    return urlId(String(value ?? '').trim());
  }

  onOrderIdChange(value: string): void {
    const id = this.fieldId(value);
    this.orderIdHref = id ? this.router.serializeUrl(this.router.createUrlTree(['/sales/quotation', +id])) : null;
  }

  onOrderIdBlur(value: string): void {
    if (!this.alive || this.leaving || this.router.currentNavigation()) return; // vue qu'on quitte : Chrome lance ce blur au retrait du DOM, avant onDestroy
    const id = this.fieldId(value);
    if (id === this.orderId && !(id && this.searchFailed)) return; // déjà affichée, ou rien de tapé hors raccourci
    if (id) this.searchOrderById(id); else this.onFilterChange(); // champ vidé : on quitte le raccourci, liste vidée, sans relire
  }

  // Comme « Id produit » en pleine page : Entrée ouvre la pièce, sans requête pour une liste d'une ligne.
  onOrderIdValidate(value: string): void {
    const id = this.fieldId(value);
    if (!id) return;
    this.leaving = true;
    this.router.navigate(['/sales/quotation', +id]).then((ok) => { if (!ok) this.leaving = false; }, () => this.leaving = false); // refusée ou en échec : on reste, le blur refonctionne
  }

  // Clic sur une ligne (ou ↳) pendant la saisie d'un id : le champ garde le focus, sinon son blur vide la liste avant le click.
  keepOrderIdFocus(e: MouseEvent): void {
    if (e.button === 0 && this.orderIdNavInput?.isFocused()) e.preventDefault();
  }

  private searchOrderById(id: string): void {
    this.enterOrderId(id);
    this.onSearch();
  }

  // La barre repasse à l'état de Réinitialiser : l'écran ne montre aucun critère que le serveur ignorerait.
  private enterOrderId(id: string): void {
    this.resetBar();
    this.orderId = id;
    this.showOrderId(id);
  }

  private showOrderId(id: string | null): void {
    this.orderIdText = id ?? '';
    this.onOrderIdChange(this.orderIdText);
  }

  // Un autre filtre reprend la main : on quitte le raccourci ; le champ garde sa saisie, comme « Id produit ».
  private leaveOrderId(): void {
    this.orderId = null;
  }

  // Statut, Magasin, Employés, Nb rép. : la liste se vide, rien ne part avant « Rechercher ».
  onFilterChange(): void {
    this.leaveOrderId();
    this.clearList();
  }

  public readonly docTypes: DocType[] = Object.values(DocType);

  docTypeName(t: string): string { return getDocTypeLabel(t); }

  get isRentalFilter(): boolean { return this.filter.doc_type === DocType.LOCATION; }

  // Un dossier de location vit des années (reprises orthop créées dès 2015) : sa date de création ne doit pas le cacher, « Du » est vidé.
  onDocTypeChange(): void {
    this.onFilterChange();
    if (this.isRentalFilter) this.filter.datstart = undefined;
  }

  dateChange(date: any, type: string): void {
    this.onFilterChange();
    if (type === 'datstart') this.filter.datstart = date;
    if (type === 'datend') this.filter.datend = date;
  }

  // Raccourcis Du/Au, tous jusqu'à aujourd'hui inclus ; appliqués même en Location, où le choix est explicite.
  public readonly periods: { key: Period; label: string; title: string }[] = [
    { key: 'day', label: 'Jour', title: "Aujourd'hui" },
    { key: 'month', label: 'Mois', title: "Mois en cours : du 1er à aujourd'hui" },
    { key: 'days30', label: '30 j', title: "30 derniers jours, aujourd'hui compris" },
    { key: 'months12', label: '12 m', title: "12 derniers mois : du lendemain de la même date l'an dernier à aujourd'hui" },
  ];

  private periodRange(key: Period): [Date, Date] {
    const today = new Date();
    const y = today.getFullYear(), m = today.getMonth(), d = today.getDate();
    if (key === 'month') return [new Date(y, m, 1), today];
    if (key === 'days30') return [new Date(y, m, d - 29), today];
    if (key === 'months12') { const s = yearsBefore(today, 1); s.setDate(s.getDate() + 1); return [s, today]; }
    return [new Date(y, m, d), today];
  }

  isPeriod(key: Period): boolean {
    const [s, e] = this.periodRange(key);
    return pickedDateStr(this.filter.datstart) === pickedDateStr(s) && pickedDateStr(this.filter.datend) === pickedDateStr(e);
  }

  setPeriod(key: Period): void {
    this.onFilterChange();
    [this.filter.datstart, this.filter.datend] = this.periodRange(key);
  }

  sortOrders(order: boolean, name: string) {
    this.orders = this.orders.sort((a: any, b: any) => {
      let valA = this.getSortValue(a, name);
      let valB = this.getSortValue(b, name);
      const isNumeric = (v: any) => !isNaN(parseFloat(v)) && isFinite(v);
      if (isNumeric(valA) && isNumeric(valB)) {
        return order ? parseFloat(valA) - parseFloat(valB) : parseFloat(valB) - parseFloat(valA);
      } else {
        return order ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
      }
    });
    this.displayedColumnsInfo = this.displayedColumnsInfo.map(col =>
      col[0] === name ? [col[0], col[1], col[2], order] : [col[0], col[1], col[2]]
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // Ordre de tri des colonnes « Statut » et « Type »
  //
  // SalesOrderState et DocType sont des enums de CHAÎNES : triées telles quelles,
  // elles tombaient dans la branche `localeCompare` de sortOrders, donc dans l'ordre
  // alphabétique des NOMS ANGLAIS de l'enum — CANCELED, CREATED, DELIVERED, INVOICED,
  // MIGRATION, PAID, PREPARATION, QUOTE, REFUSED. Un ordre qui n'a aucun sens dans une
  // IHM française, et qui n'était ni l'ordre des id, ni celui du cycle de vie.
  //
  // On trie donc sur le RANG dans l'enum. Pour les états, ce rang EST l'id du serveur :
  // constants.toml, [sales_order_states] — canceled=0, created=1, quote=2, preparation=3,
  // delivered=4, invoiced=5, paid=6, refused=7, migration=8 — et le générateur préserve
  // cet ordre de déclaration. Pour les types de pièce, doc_type n'a pas d'id serveur
  // (le bloc [doc_type] de constants.toml est commenté) : c'est l'ordre de déclaration
  // de l'enum du front qui fait foi.
  //
  // Ces deux listes alimentent AUSSI les infobulles d'entête : le classement affiché à
  // l'utilisateur et celui appliqué au tri viennent de la même source, ils ne peuvent
  // pas diverger.
  // ═══════════════════════════════════════════════════════════════════════════════
  private readonly STATE_RANK: string[] = Object.values(SalesOrderState);
  private readonly DOCTYPE_RANK: string[] = Object.values(DocType);

  // Une valeur inconnue ou absente est renvoyée en fin de tri, jamais en tête.
  private rankOf(order: string[], value: any): number {
    const i = order.indexOf(String(value ?? '').toUpperCase());
    return i === -1 ? order.length : i;
  }

  // Infobulles d'entête : elles ÉNONCENT l'ordre réellement appliqué, pour que le
  // classement ne paraisse pas arbitraire. Construites une fois, à l'instanciation.
  public readonly statusSortHint: string =
    'Tri par cycle de vie du document (ni alphabétique, ni par date) :\n' +
    this.STATE_RANK.map((s, i) => `${i + 1}. ${getOrderLabel(s)}`).join('\n');

  public readonly typeSortHint: string =
    'Tri par ordre des types de pièce :\n' +
    this.DOCTYPE_RANK.map((t, i) => `${i + 1}. ${getDocTypeLabel(t)}`).join('\n');

  // Le gabarit rend les entêtes génériquement depuis displayedColumnsInfo : c'est ici
  // qu'on dit quelles colonnes méritent une explication. '' = pas d'infobulle affichée.
  colSortHint(colKey: string): string {
    if (colKey === 'status') return this.statusSortHint;
    if (colKey === 'type') return this.typeSortHint;
    if (colKey === 'store') return this.storeLegend;
    return '';
  }

  // La colonne « E » ne tient qu'un NUMÉRO d'établissement : la correspondance
  // id → nom n'est écrite nulle part sur la page. On la donne en infobulle d'entête,
  // construite une fois à l'arrivée du référentiel (le gabarit interroge colSortHint()
  // à chaque cycle de détection : rien ne doit y être recalculé).
  private buildStoreLegend(): string {
    const lignes = this.stores
      .filter(store => (store.id ?? -1) > 0)
      .map(store => `${store.id} — ${store.name ?? ''}`.trim());
    return lignes.length ? 'Établissements :\n' + lignes.join('\n') : '';
  }

  getSortValue(p: any, name: string): any {
    switch (name) {
      case 'customer': return (p.lnk_customer?.name ?? '') + ' ' + (p.lnk_customer?.firstname ?? '');
      // Rang dans l'enum, PAS la chaîne elle-même : voir STATE_RANK / DOCTYPE_RANK.
      case 'status': return this.rankOf(this.STATE_RANK, p.state);
      case 'amount_taxi': return Number(p.amount_taxi);
      case 'datecre': return p.dates?.created ? new Date(p.dates.created).getTime() : 0;
      // getLastDateTime rend déjà un instant : pas de re-parsing. L'ancienne écriture
      // (`new Date(...).getTime() ?? 0`) laissait passer NaN — `??` ne rattrape que null
      // et undefined —, et sortOrders retombait alors sur un tri de la chaîne « NaN ».
      case 'dateval': { const t = this.getLastDateTime(p); return isNaN(t) ? 0 : t; }
      case 'store': return p.lnk_station?.store ?? '';
      // `p.data?.typ` était un reliquat de l'ancien modèle (1 = comptoir, 3 = livraison) :
      // il n'est plus alimenté, donc TOUTES les lignes avaient la même clé et le clic sur
      // « Type » ne triait rien. La colonne AFFICHE `doc_type` : c'est lui qu'on trie.
      case 'type': {
        const baseRank = this.rankOf(this.DOCTYPE_RANK, p.doc_type);
        return this.isRentalChild(p) ? baseRank + 0.1 : baseRank;
      }
      case 'user': return this.getUserShortName(p.id_user);
      case 'amount_remainer': return this.getRemainer(p);
      default: return p[name];
    }
  }

  getUserShortName(id_user: Number): string {
    let user = this.users.find(user => user.id === id_user);
    if (user) { return (user.firstname ?? '') + ' ' + (user.lastname?.[0] ?? ''); }
    else { return ''; }
  }

  showgetRemainer(p: any) {
    let remainer = this.getRemainer(p);
    if (remainer == 0) return '';
    return remainer.toString().replace('.', ',');
  }

  // TODO check this out. receivable refactoring
  getRemainer(p: any) {
    // Annulée : plus rien n'est dû (avoir et contre-passations), même si ses créances closes gardent attendu et payé.
    if (p.state == this.SalesOrderState.PAID || p.state == this.SalesOrderState.CANCELED) return 0;
    if (p && p.lnk_receivables && p.lnk_receivables.length > 0) {
      let expected = 0;
      let paid = 0;
      for (let i = 0; i < p.lnk_receivables.length; i++) {
        expected += +p.lnk_receivables[i].amount_expected;
        paid += +p.lnk_receivables[i].amount_paid;
      }
      let return_val = (Math.round((expected - paid) * 100) / 100)
      return return_val <= 0 ? 0 : return_val;
    }
    if (p.state == this.SalesOrderState.MIGRATION) {
      // Document repris d'orthop, créances non chargées en liste : soldé =>
      // dates.paid posé par la reprise ; sinon le reste dû d'orthop (fac_du_*)
      // est conservé dans data.old_orthop. À défaut, repli inchangé.
      if (p.dates?.paid) return 0;
      const old = p.data?.old_orthop;
      if (old) {
        const du = (+old.fac_du_client || 0) + (+old.fac_du_secu || 0) + (+old.fac_du_mutu || 0);
        const val = Math.round(du * 100) / 100;
        return val <= 0 ? 0 : val;
      }
    }
    return p.amount_taxi;
  }

  /**
   * Retourne l'identifiant du contrat parent s'il existe dans data.parent_order_id.
   */
  getParentOrderId(o: any): number | null {
    const pid = o?.data?.parent_order_id;
    if (!pid) return null;
    const n = Number(pid);
    return isNaN(n) ? null : n;
  }

  /**
   * Retourne l'identifiant de l'avoir s'il existe dans data.avoir_order_id.
   */
  getAvoirOrderId(o: any): number | null {
    const aid = o?.data?.avoir_order_id;
    if (!aid) return null;
    const n = Number(aid);
    return isNaN(n) ? null : n;
  }

  // Infobulle de la flèche ↳ : échéance, avoir ou acompte selon le type, comme le bandeau du survol.
  parentTooltip(o: any, parentId: number): string {
    const [texte, lien] = parentOrderLabel(o?.doc_type);
    return `${texte} ${lien.toLowerCase()} #${parentId}`;
  }

  /**
   * Indique si la pièce est un contrat mère de location (doc_type = LOCATION).
   */
  isRentalContract(o: any): boolean {
    return o?.doc_type === DocType.LOCATION;
  }

  /**
   * Indique si la pièce est une facture fille / échéance issue d'un contrat de location.
   * Ni avoir (son parent est la pièce qu'il annule, FAC-AC comprise) ni acompte : même règle qu'isRentalChildOrder de l'écran de commande.
   */
  isRentalChild(o: any): boolean {
    return (o?.doc_type === DocType.FACTURE || o?.doc_type === DocType.FACTURE_FSE) && this.getParentOrderId(o) !== null;
  }

  /**
   * Ouvre directement la fiche du contrat parent sans déclencher le clic de ligne.
   */
  goToParentOrder(event: MouseEvent, parentId: number | null): void {
    if (!parentId) return;
    event.stopPropagation();
    event.preventDefault();
    // Nouvel onglet comme la ligne (JP, 26/10) : la liste et ses filtres restent dans celui-ci.
    const url = this.router.serializeUrl(this.router.createUrlTree(['/sales/quotation', parentId]));
    if (!window.open(url, '_blank')) this.alertService.error('Onglet bloqué par le navigateur — autorisez les pop-ups pour ce site');
  }

  // Libellé français du type de pièce.
  // Distingue spécifiquement les contrats mères (« Contrat Loc. ») et leurs échéances filles (« Échéance Loc »).
  docTypeLabel(o: any): string {
    if (!o?.doc_type) return '';
    if (this.isRentalContract(o)) return 'Contrat Loc.';
    if (this.isRentalChild(o)) return 'Échéance Loc';
    return getDocTypeLabel(o.doc_type);
  }

  docTypeTooltip(o: any): string {
    if (this.isRentalContract(o)) return 'Contrat de location parent';
    const pid = this.getParentOrderId(o);
    if (this.isRentalChild(o) && pid) return `Facture d'échéance issue du contrat de location #${pid}`;
    return this.docTypeLabel(o);
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Totaux de la barre de liste : sommes des commandes AFFICHÉES — donc tronquées
  // par `filter.limit`, d'où les libellés « … affiché » et les infobulles du gabarit.
  //
  // Mémoïsés sur la RÉFÉRENCE de `orders`, et non recalculés à chaque cycle de
  // détection de changements : avec la limite à 5000, deux getters naïfs dans le
  // gabarit feraient 10 000 additions par cycle. `onSearch` réaffecte `orders` (le
  // cache tombe), tandis que `sortOrders` trie EN PLACE et renvoie le même tableau —
  // la référence ne bouge pas, et c'est correct : une somme ne dépend pas de l'ordre.
  // ─────────────────────────────────────────────────────────────────────────────
  private kpiSource: any[] | null = null;
  private kpiCache = { total: 0, remainer: 0 };

  private kpi(): { total: number, remainer: number } {
    if (this.kpiSource !== this.orders) {
      let total = 0, remainer = 0;
      for (const o of this.orders) {
        total += Number(o.amount_taxi) || 0;
        remainer += Number(this.getRemainer(o)) || 0;
      }
      this.kpiSource = this.orders;
      this.kpiCache = { total: Math.round(total * 100) / 100, remainer: Math.round(remainer * 100) / 100 };
    }
    return this.kpiCache;
  }

  get totalAmount(): number { return this.kpi().total; }
  get totalRemainer(): number { return this.kpi().remainer; }

  getColor(state: any): string {
    // Cast to the enum type here where TS is happy
    return this.salesOrderColors[state as SalesOrderState] || 'Inconnu';
  }
  // Clés « métier » de sales_order.dates : les états du CYCLE DE VIE de la pièce
  // (SalesOrderState::json_key, mercalink-server/common/src/dao.rs — c'est la liste
  // exhaustive, il n'existe pas d'autre source de clés).
  //
  // MIGRATION en est exclu : ce n'est pas un événement de la pièce, c'est l'horodatage du
  // batch de reprise (_batch/migorthop/src/sales/mod.rs), identique à la minute près pour
  // toutes les pièces importées. Le laisser dans le calcul faisait afficher la date de
  // reprise à la place de la dernière date métier, et un simple rejeu du batch faisait
  // « bouger » la colonne sur tout le stock repris.
  //
  // La clé DOIT rester en base : la fiche l'affiche à côté de la pastille d'état
  // (quotation.component.ts, datCurrentState). C'est bien ici qu'on l'ignore.
  private readonly DATE_KEYS_METIER: string[] = Object.values(SalesOrderState)
    .filter(s => s !== SalesOrderState.MIGRATION)
    .map(s => getStateKey(s)!);

  // Trois écrivains alimentent `dates`, dans TROIS formats : l'insert applicatif
  // (now()::timestamp — naïf), les transitions d'état (to_jsonb(now()) sur un timestamptz —
  // ISO AVEC offset) et la reprise (naïf, ou date nue « ...T00:00:00 »). Comparer ces
  // chaînes entre elles n'a pas de sens : on parse en instants avant de comparer.
  private parseDate(raw: any): number {
    if (!raw) return NaN;
    if (raw instanceof Date) return raw.getTime();
    if (typeof raw !== 'string') return NaN;
    let normalized = raw;
    if (/(?:[+-]\d{2})$/.test(normalized)) normalized += ':00';   // offset tronqué « +02 »
    return Date.parse(normalized);
  }

  // La plus récente des dates métier : son instant (pour trier) ET sa valeur brute (pour
  // l'affichage — le DatePipe gère offset et fractions de seconde mieux qu'un aller-retour
  // par Date). Un seul parcours, pour que tri et affichage ne puissent pas diverger.
  private lastDate(o: any): { t: number, raw: string | null } {
    const dates = o?.dates;
    if (!dates) return { t: NaN, raw: null };
    let bestT = NaN, bestRaw: string | null = null;
    for (const k of this.DATE_KEYS_METIER) {
      const raw = dates[k];
      const t = this.parseDate(raw);
      if (isNaN(t)) continue;
      if (isNaN(bestT) || t > bestT) { bestT = t; bestRaw = raw; }
    }
    return { t: bestT, raw: bestRaw };
  }

  getLastDateTime(o: any): number { return this.lastDate(o).t; }

  getLastDate(o: any): string | null { return this.lastDate(o).raw; }

}
